import React, { useEffect, useState, useRef } from 'react';
import { SearchIcon, XIcon, CheckIcon } from 'lucide-react';
import { useDarkClasses } from '../../hooks/useDarkClasses';
import { useUserRole } from '../../context/UserRoleContext';
interface MultiSelectSearchProps {
  options: string[];
  selected: string[];
  onChange: (selected: string[]) => void;
  placeholder?: string;
  label?: string;
  className?: string;
}
export const MultiSelectSearch: React.FC<MultiSelectSearchProps> = ({
  options,
  selected,
  onChange,
  placeholder = 'Search...',
  label,
  className = ''
}) => {
  const dk = useDarkClasses();
  const {
    isReader
  } = useUserRole();
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  // Filter options based on search query and exclude already selected items
  const filteredOptions = options.filter(option => option.toLowerCase().includes(searchQuery.toLowerCase()) && !selected.includes(option));
  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  const handleSelect = (option: string) => {
    onChange([...selected, option]);
    setSearchQuery('');
    inputRef.current?.focus();
  };
  const handleRemove = (optionToRemove: string) => {
    onChange(selected.filter(option => option !== optionToRemove));
  };
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && filteredOptions.length > 0) {
      e.preventDefault();
      handleSelect(filteredOptions[0]);
    } else if (e.key === 'Backspace' && searchQuery === '' && selected.length > 0) {
      handleRemove(selected[selected.length - 1]);
    } else if (e.key === 'Escape') {
      setIsOpen(false);
      inputRef.current?.blur();
    }
  };
  // Role-based colors
  const focusRingClass = isReader ? 'focus:ring-indigo-500 focus:border-indigo-500' : 'focus:ring-purple-500 focus:border-purple-500';
  const chipBgClass = isReader ? dk.isDark ? 'bg-indigo-900/40 text-indigo-300' : 'bg-indigo-100 text-indigo-800' : dk.isDark ? 'bg-purple-900/40 text-purple-300' : 'bg-purple-100 text-purple-800';
  const optionHoverClass = isReader ? dk.isDark ? 'hover:bg-indigo-900/20' : 'hover:bg-indigo-50' : dk.isDark ? 'hover:bg-purple-900/20' : 'hover:bg-purple-50';
  return <div className={`w-full ${className}`} ref={wrapperRef} data-id="element-3818" data-mp="2yjz3de3htsd8">
      {label && <label className={`block text-sm font-medium mb-2 ${dk.isDark ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-3819" data-mp="2tkm9i9e7fl15">
          {label}
        </label>}

      <div className={`relative min-h-[42px] w-full rounded-md border px-2 py-1.5 flex flex-wrap items-center gap-2 transition-colors ${dk.isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'} focus-within:ring-2 focus-within:ring-offset-0 ${focusRingClass.split(' ')[0]}`} onClick={() => {
      setIsOpen(true);
      inputRef.current?.focus();
    }} data-id="element-3820" data-mp="311rfdlsq4mqg">
        {/* Selected Chips */}
        {selected.map(item => <span key={item} className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium ${chipBgClass}`} data-id="element-3821" data-mp="2n66wurgwq6jk">
            {item}
            <button type="button" onClick={e => {
          e.stopPropagation();
          handleRemove(item);
        }} className={`ml-1.5 inline-flex items-center justify-center rounded-full hover:bg-black/10 dark:hover:bg-white/10 focus:outline-none`} data-id="element-3822" data-mp="2w6c4n2q0j6y9">
              <XIcon className="h-3 w-3" data-id="element-3823" data-mp="9z7jljsrxbfm" />
            </button>
          </span>)}

        {/* Search Input */}
        <div className="flex-1 min-w-[120px] relative" data-id="element-3824" data-mp="jvak6l7z920g">
          <input ref={inputRef} type="text" className={`w-full bg-transparent border-none p-0 text-sm focus:ring-0 placeholder-gray-400 ${dk.isDark ? 'text-white' : 'text-gray-900'}`} placeholder={selected.length === 0 ? placeholder : ''} value={searchQuery} onChange={e => {
          setSearchQuery(e.target.value);
          setIsOpen(true);
        }} onFocus={() => setIsOpen(true)} onKeyDown={handleKeyDown} data-id="element-3825" data-mp="jwvj0n1br744" />
        </div>

        {/* Search Icon Indicator */}
        <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none" data-id="element-3826" data-mp="1bn9p3x33jf58">
          <SearchIcon className={`h-4 w-4 ${dk.isDark ? 'text-gray-400' : 'text-gray-400'}`} data-id="element-3827" data-mp="17mptixy68jl7" />
        </div>
      </div>

      {/* Dropdown Options */}
      {isOpen && (filteredOptions.length > 0 || searchQuery) && <div className={`absolute z-50 mt-1 w-full rounded-md shadow-lg max-h-60 overflow-auto py-1 text-base ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm ${dk.isDark ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'}`} data-id="element-3828" data-mp="1h8r18bwdebwb">
          {filteredOptions.length > 0 ? filteredOptions.map(option => <div key={option} className={`cursor-pointer select-none relative py-2 pl-3 pr-9 ${dk.isDark ? 'text-gray-200' : 'text-gray-900'} ${optionHoverClass}`} onClick={() => handleSelect(option)} data-id="element-3829" data-mp="1cbqv60zit957">
                <span className="block truncate" data-id="element-3830" data-mp="oleeo1q7m430">{option}</span>
              </div>) : <div className={`cursor-default select-none relative py-2 pl-3 pr-9 ${dk.isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-3831" data-mp="tin0qdehas9j">
              No results found for "{searchQuery}"
            </div>}
        </div>}
    </div>;
};